import { type AuthSession } from '@resurs-internal/deposits-app-components';

import {
  getAccessToken,
  refreshAccessToken,
  revokeAccessToken,
  revokeRefreshToken,
} from '@/services';
import { type AuthTokenResponse } from '@/types/oba-auth-service';

import { removeKycSession } from './kycUtils';

export const setAuthSession = (session: AuthSession) => {
  sessionStorage.setItem('accessToken', session.accessToken);
  sessionStorage.setItem('refreshToken', session.refreshToken);
  sessionStorage.setItem('expiresAt', session.expiresAt.toString());

  window.dispatchEvent(new Event('storage'));
};

export const clearAuthSession = () => {
  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('expiresAt');
  removeKycSession();
};

export const getAuthSession = (): AuthSession => {
  const accessToken = sessionStorage.getItem('accessToken');
  const expiresAt = Number(sessionStorage.getItem('expiresAt'));
  const refreshToken = sessionStorage.getItem('refreshToken');

  if (
    accessToken &&
    expiresAt &&
    refreshToken &&
    expiresAt - new Date().getTime() >= 0
  )
    return { accessToken, expiresAt, refreshToken };
  else throw Error('Not authenticated');
};

export const revokeAccessTokens = async () => {
  try {
    const { accessToken, refreshToken } = getAuthSession();

    return await Promise.all([
      revokeAccessToken(accessToken),
      revokeRefreshToken(refreshToken),
    ]);
  } catch (e) {
    if (e instanceof Error && e.message === 'Not authenticated') {
      return;
    }
    throw e;
  }
};

export const logout = async () => {
  return await revokeAccessTokens()
    .catch(() => console.error('Failed to revoke tokens!'))
    .finally(clearAuthSession);
};

export const setAuthSessionFromCode = async (
  code: string,
  callbackState: string,
) => {
  if (!isAuthenticated()) {
    const storedState = sessionStorage.getItem('auth_state');
    const codeVerifier = sessionStorage.getItem('code_verifier');

    if (callbackState !== storedState) {
      clearAuthSession();

      return Promise.reject('Authentication failed: invalid state parameter.');
    } else if (!code) {
      clearAuthSession();

      return Promise.reject(
        'Authentication failed: missing authorization code.',
      );
    } else if (!codeVerifier) {
      clearAuthSession();

      return Promise.reject(
        'Authentication failed: missing PKCE code verifier.',
      );
    } else {
      return getAccessToken(code, codeVerifier)
        .then((response) => {
          sessionStorage.removeItem('auth_state');
          sessionStorage.removeItem('code_verifier');
          parseResponse(response);

          return Promise.resolve();
        })
        .catch(() => {
          clearAuthSession();

          return Promise.reject(
            'Authentication failed: unable to obtain access token.',
          );
        });
    }
  }
};
export const refreshAuthSession = () => {
  try {
    const session = getAuthSession();

    refreshAccessToken(session.refreshToken)
      .then(parseResponse)
      .catch((error) => {
        console.error(JSON.stringify(error));
        logout();
      });
  } catch {
    throw Error('Cannot refresh session, not authenticated');
  }
};

export const isAuthenticated = (): boolean => {
  try {
    getAuthSession();

    return true;
  } catch {
    return false;
  }
};

const parseResponse = (response: AuthTokenResponse) => {
  const accessToken = response?.access_token;
  const refreshToken = response?.refresh_token;
  const expiresIn = response?.refresh_token_expires_in;

  if (
    typeof accessToken !== 'string' ||
    typeof refreshToken !== 'string' ||
    typeof expiresIn !== 'number'
  ) {
    clearAuthSession();
    logout();
    throw new Error('Something went wrong');
  }

  setAuthSession({
    accessToken,
    expiresAt: new Date().getTime() + expiresIn * 1000,
    refreshToken,
  });
};
